import moment from 'moment';
import { LevelRewardInput, LevelRewardInputType, RedeemCodeFragmentFragment } from '../../../../../__gqltypes__';
import { typeEmoji } from '../../components/formatReward';

export type RedeemCodeReward = RedeemCodeFragmentFragment['rewards'][number];

// These fields are not in the generated __gqltypes__ until `yarn codegen` runs against a deployed backend
export type RedeemCodeItem = RedeemCodeFragmentFragment & {
  enabledIos: boolean;
  enabledAndroid: boolean;
  filterAppVersionOperator?: string | null;
  filterUserLevel?: number | null;
  filterUserMinCreationDate?: string | Date | null;
  filterUserMaxCreationDate?: string | Date | null;
};

export const APP_VERSION_OPERATORS = ['>=', '>', '=', '!=', '<=', '<'] as const;

export const REDEEM_CODE_REGEX = /^[A-Z0-9]+$/;

export const isValidRedeemCode = (code: string): boolean => REDEEM_CODE_REGEX.test(code.trim());

export type EditableReward = LevelRewardInput & { itemName?: string };

const TYPENAME_ALIASES: Record<string, LevelRewardInputType> = {
  CurrencyUnlimitedBoost: LevelRewardInputType.CURRENCY_BOOST,
  CurrencyCategoryRandom: LevelRewardInputType.CURRENCY_CATEGORY_FRAGMENT,
};

const toLevelRewardInputType = (typename: string): LevelRewardInputType | null => {
  if (TYPENAME_ALIASES[typename]) {
    return TYPENAME_ALIASES[typename];
  }
  const normalized = typename
    .replace(/([A-Z])/g, '_$1')
    .replace(/^_/, '')
    .toUpperCase();
  const asEnum = normalized as LevelRewardInputType;
  return Object.values(LevelRewardInputType).includes(asEnum) ? asEnum : null;
};

// Maps a LevelReward union member (as queried) back to the LevelRewardInput shape used by EditRewardsModal
export const rewardUnionToInput = (reward: RedeemCodeReward): EditableReward | null => {
  const type = toLevelRewardInputType(reward.__typename);
  if (!type) {
    return null;
  }

  let itemId: string | null = null;
  if ('boostType' in reward && reward.boostType) {
    itemId = reward.boostType;
  } else if ('category' in reward && reward.category) {
    itemId = reward.category.id;
  } else if ('contentPack' in reward && reward.contentPack) {
    itemId = reward.contentPack.id;
  } else if ('id' in reward && reward.id) {
    itemId = reward.id;
  }

  return {
    type,
    amount: 'amount' in reward && typeof reward.amount === 'number' ? reward.amount : 1,
    itemId,
    ...('name' in reward && reward.name ? { itemName: reward.name } : {}),
  };
};

export const parseFilterUserIds = (raw: string): string[] | null => {
  const ids = raw
    .split(/[\n,;]+/)
    .map((id) => id.trim())
    .filter((id) => id.length > 0);
  return ids.length > 0 ? ids : null;
};

export const formatUtcDate = (date: number | Date | null | undefined): string =>
  date ? moment(date).utc().format('YYYY-MM-DD HH:mm') : '-';

export const formatLocalPreview = (date: Date | null | undefined): string =>
  date ? `${moment(date).local().format('YYYY-MM-DD HH:mm')} (${Intl.DateTimeFormat().resolvedOptions().timeZone})` : '';

// Short reward label for the list table, e.g. "💎 50" or "🎇 sticker_1"
export const compactRewardLabel = (reward: EditableReward): string => {
  const emoji = typeEmoji[reward.type as keyof typeof typeEmoji] ?? '';
  const detail = reward.type.startsWith('CURRENCY') ? `${reward.amount}` : reward.itemName ?? reward.itemId ?? reward.type;
  return `${emoji} ${detail}`.trim();
};

export const formatEligibilitySummary = (redeemCode: RedeemCodeItem): string => {
  const parts: string[] = [];
  if (redeemCode.filterUserIds?.length) {
    parts.push(`${redeemCode.filterUserIds.length} user${redeemCode.filterUserIds.length > 1 ? 's' : ''}`);
  }
  if (redeemCode.enabledIos !== redeemCode.enabledAndroid) {
    parts.push(redeemCode.enabledIos ? 'iOS only' : 'Android only');
  }
  if (redeemCode.filterAppVersion) {
    const operator = redeemCode.filterAppVersionOperator ?? '>=';
    parts.push(operator === '>=' ? `v${redeemCode.filterAppVersion}+` : `v ${operator} ${redeemCode.filterAppVersion}`);
  }
  if (redeemCode.filterSubscriptionTier) {
    parts.push(`${redeemCode.filterSubscriptionTier}+`);
  }
  if (redeemCode.filterUserLevel) {
    parts.push(`lvl ${redeemCode.filterUserLevel}+`);
  }
  if (redeemCode.filterUserMinCreationDate) {
    parts.push(`reg ≥ ${moment(redeemCode.filterUserMinCreationDate).utc().format('YYYY-MM-DD')}`);
  }
  if (redeemCode.filterUserMaxCreationDate) {
    parts.push(`reg ≤ ${moment(redeemCode.filterUserMaxCreationDate).utc().format('YYYY-MM-DD')}`);
  }
  return parts.length ? parts.join(' · ') : 'All players';
};

export type RedeemCodeStatusInfo = { label: string; variant: string };

export const getRedeemCodeStatus = (
  startDate: number | Date,
  endDate: number | Date | null | undefined,
  enabled = true
): RedeemCodeStatusInfo => {
  if (!enabled) {
    return { label: 'Disabled', variant: 'dark' };
  }
  if (moment(startDate).isAfter(moment())) {
    return { label: 'Pending', variant: 'warning' };
  }
  // No end date = lifetime code, never expires
  if (endDate && moment(endDate).isBefore(moment())) {
    return { label: 'Expired', variant: 'secondary' };
  }
  return { label: 'Running', variant: 'success' };
};
